import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Globe, Save, Loader2, Info, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const PortalSettingsPage = () => {
  const { api } = useAuth();
  const [settings, setSettings] = useState({ portal_url: '', stb_model: 'MAG322' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const stbModels = [
    { value: 'MAG250', label: 'MAG 250' },
    { value: 'MAG254', label: 'MAG 254' },
    { value: 'MAG256', label: 'MAG 256' },
    { value: 'MAG322', label: 'MAG 322' },
    { value: 'MAG324', label: 'MAG 324' },
    { value: 'MAG420', label: 'MAG 420' },
    { value: 'MAG424', label: 'MAG 424' },
  ];

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/portal/settings');
        setSettings(response.data);
      } catch (error) {
        toast.error('Failed to load portal settings');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [api]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/portal/settings', settings);
      toast.success('Portal settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="portal-settings-page" className="animate-fade-in max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Portal Settings</h1>
        <p className="text-muted-foreground">Configure your Stalker Portal connection</p>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6 flex gap-3">
        <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="text-blue-400 font-medium mb-1">Portal URL Format</p>
          <p className="text-blue-300/80">
            Enter the full portal URL provided by your IPTV service. Common formats:
          </p>
          <ul className="text-blue-300/70 mt-2 space-y-1 font-mono text-xs">
            <li>• http://example.com/stalker_portal/c/</li>
            <li>• http://example.com/c/</li>
            <li>• http://example.com:8080/c/</li>
          </ul>
        </div>
      </div>

      <form onSubmit={handleSave} className="dashboard-card">
        <div className="space-y-6">
          {/* Portal URL */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Portal URL
            </label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                data-testid="portal-url-input"
                type="url"
                value={settings.portal_url}
                onChange={(e) => setSettings(prev => ({ ...prev, portal_url: e.target.value }))}
                placeholder="http://your-portal.com/c/"
                className="portal-input pl-11"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              This URL will be used by all devices to connect to your IPTV service
            </p>
          </div>

          {/* STB Model */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              STB Model Emulation
            </label>
            <select
              data-testid="stb-model-select"
              value={settings.stb_model}
              onChange={(e) => setSettings(prev => ({ ...prev, stb_model: e.target.value }))}
              className="w-full bg-background border border-border rounded-lg py-3 px-4 text-foreground focus:border-primary focus:ring-1 focus:ring-primary transition-all"
            >
              {stbModels.map(model => (
                <option key={model.value} value={model.value}>
                  {model.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-2">
              Select the MAG device model to emulate. MAG 322 is recommended for best compatibility.
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-8 pt-6 border-t border-border">
          <button
            data-testid="save-portal-settings-button"
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </form>

      {/* Connection Status */}
      {settings.portal_url && (
        <div className="mt-6 dashboard-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="font-medium text-foreground">Portal Configured</p>
              <p className="text-sm text-muted-foreground">
                Devices can now connect using their access codes
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalSettingsPage;
